import React, { useEffect, useMemo, useState } from "react";
import styles from "./RentPages.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { fetchProperties } from "../../redux/propertiesSlice";
import { RootState, AppDispatch } from "../../redux/store";
import { Property } from "../../types/property";
import {
  DEFAULT_FILTERS,
  FilterWithCollapse,
  PropertyFilters,
  applyPropertyFilters,
} from "./Filter";
import {
  getAvailabilityLabel,
  monthlyRentPcm,
} from "../../util/propertyParsing";
import { ViewSwitcher } from "./PropertiesPage";
import { useSearchParams } from "react-router-dom";
import AreaGuideTeaser from "src/components/AreaGuide/AreaGuideTeaser";
import { TenantGuides } from "./RentPage";
import AboutAndContactForm from "src/components/AboutAndContactForm/AboutAndContactForm";
import PropertySaveButton from "../../components/PropertyActions/PropertySaveButton.tsx";
import PropertyShareButtons from "../../components/PropertyActions/PropertyShareButtons.tsx";
import CompareTray from "../../components/PropertyActions/CompareTray.tsx";
import EmailAlertForm from "../../components/EmailAlertForm/EmailAlertForm.tsx";
import { propertyBrochureUrl } from "../../api/properties";
import { Download } from "lucide-react";
import SafeImage from "../../components/SafeImage/SafeImage";

const getMainImage = (property: Property) => {
  // If media is present and the first media_url is not empty, return it.
  if (
    property.media &&
    property.media.length > 0 &&
    property.media[0].media_url
  ) {
    return property.media[0].media_url;
  }
  return "https://via.placeholder.com/800x600";
};

const ListViewPage: React.FC = () => {
  const [appliedFilters, setAppliedFilters] =
    useState<PropertyFilters>(DEFAULT_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortOrder, setSortOrder] = useState<
    "none" | "asc" | "desc" | "latest"
  >("none");
  const dispatch = useDispatch<AppDispatch>();
  const {
    items: properties,
    status,
    error,
  } = useSelector((state: RootState) => state.properties);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchProperties());
    }
  }, [dispatch, status]);

  // hydrate filters/sort from URL
  useEffect(() => {
    const params = searchParams;
    const num = (key: string): number | null => {
      const v = params.get(key);
      if (v === null || v === "") return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };
    const bool = (key: string): boolean | null => {
      const v = params.get(key);
      if (v === null || v === "") return null;
      return v === "true";
    };
    const next: PropertyFilters = {
      propertyType: params.get("propertyType") || "",
      availableFrom: params.get("availableFrom") || "",
      minPrice: num("minPrice"),
      maxPrice: num("maxPrice"),
      bedrooms: num("bedrooms"),
      bathrooms: num("bathrooms"),
      isFurnished: bool("isFurnished"),
      newHome: bool("newHome"),
      status: params.get("status") || DEFAULT_FILTERS.status,
      minArea: num("minArea"),
      minDeposit: num("minDeposit"),
      maxDeposit: num("maxDeposit"),
      keyword: params.get("keyword") || "",
    };
    setAppliedFilters(next);
    const s = params.get("sort");
    if (s === "asc" || s === "desc" || s === "latest") {
      setSortOrder(s);
    } else {
      setSortOrder("none");
    }
  }, [searchParams]);

  const updateSearchParams = (
    filters: PropertyFilters,
    sort: "none" | "asc" | "desc" | "latest"
  ) => {
    const params = new URLSearchParams();
    if (filters.propertyType) params.set("propertyType", filters.propertyType);
    if (filters.availableFrom)
      params.set("availableFrom", filters.availableFrom);
    if (filters.minPrice !== null)
      params.set("minPrice", String(filters.minPrice));
    if (filters.maxPrice !== null)
      params.set("maxPrice", String(filters.maxPrice));
    if (filters.bedrooms !== null)
      params.set("bedrooms", String(filters.bedrooms));
    if (filters.bathrooms !== null)
      params.set("bathrooms", String(filters.bathrooms));
    if (filters.isFurnished !== null)
      params.set("isFurnished", String(filters.isFurnished));
    if (filters.newHome !== null)
      params.set("newHome", String(filters.newHome));
    if (filters.status && filters.status !== DEFAULT_FILTERS.status)
      params.set("status", filters.status);
    if (filters.minArea !== null)
      params.set("minArea", String(filters.minArea));
    if (filters.minDeposit !== null)
      params.set("minDeposit", String(filters.minDeposit));
    if (filters.maxDeposit !== null)
      params.set("maxDeposit", String(filters.maxDeposit));
    if (filters.keyword.trim()) params.set("keyword", filters.keyword.trim());
    if (sort !== "none") params.set("sort", sort);
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    const base = applyPropertyFilters(properties, appliedFilters);
    if (sortOrder === "asc") {
      return [...base].sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    }
    if (sortOrder === "desc") {
      return [...base].sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    }
    if (sortOrder === "latest") {
      return [...base].sort((a, b) => {
        const aDate = a.date_available
          ? new Date(a.date_available).getTime()
          : 0;
        const bDate = b.date_available
          ? new Date(b.date_available).getTime()
          : 0;
        return bDate - aDate;
      });
    }
    return base;
  }, [properties, appliedFilters, sortOrder]);

  return (
    <div className="properties-container">
      <div className="fixed inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,_transparent_1px),linear-gradient(90deg,_rgba(0,0,0,0.03)_1px,_transparent_1px)] bg-[40px_40px] transform rotate-[var(--angle)] scale-150 z-[-1] pointer-events-none"></div>

      {/* SEARCH CONSOLE */}
      <header className="sticky top-[70px] z-50 bg-[rgba(255,255,255,0.92)] backdrop-blur-md py-2 px-4 sm:px-6 border-b border-b-solid border-b-[#ddd]">
        <FilterWithCollapse
          value={appliedFilters}
          onApply={(next) => {
            setAppliedFilters(next);
            updateSearchParams(next, sortOrder);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          showAdvanced={showAdvanced}
          onToggle={setShowAdvanced}
          compact
          className="w-full"
        />
      </header>

      {/* RESULTS */}
      <section
        className={`${styles["results-container"]} p-16 py-8 px-[10%] max-w-[1400px] mx-auto`}
      >
        <div className="flex justify-end z-50">
          <ViewSwitcher />
        </div>
        <div className="flex justify-between items-end mb-8 border-b-2 border-b-solid border-b-brand-yellow pb-4 gap-4 flex-wrap">
          <div>
            <span className="font-teko text-3xl leading-tight">
              {filtered.length}
            </span>
            <span className="block text-sm uppercase tracking-wider">
              Available Units
            </span>
          </div>
          <div className="text-sm uppercase tracking-wider flex items-center gap-2">
            <label htmlFor="sortSelect">Sorted By:</label>
            <select
              id="sortSelect"
              value={sortOrder}
              onChange={(e) => {
                const next = e.target.value as
                  | "none"
                  | "asc"
                  | "desc"
                  | "latest";
                setSortOrder(next);
                updateSearchParams(appliedFilters, next);
              }}
              className="border border-solid border-[#ddd] px-3 py-1 text-xs uppercase tracking-wider bg-white"
            >
              <option value="none">Default</option>
              <option value="asc">Price Low → High</option>
              <option value="desc">Price High → Low</option>
              <option value="latest">Available Date (Newest)</option>
            </select>
          </div>
        </div>

        {status === "failed" && (
          <div className="text-red-600 mb-4">{error || "Failed to load"}</div>
        )}

        {status !== "succeeded" && status !== "failed" && (
          <div className="text-text-grey mb-4">Loading properties...</div>
        )}

        {status === "succeeded" && filtered.length === 0 && (
          <div className="text-text-grey bg-[#f9f9f9] border border-dashed border-[#ddd] p-6 rounded">
            No properties match the selected filters. Try widening your
            criteria.
          </div>
        )}

        {filtered.map((property) => {
          const propertyUrl =
            typeof window !== "undefined"
              ? `${window.location.origin}/services/rent/properties/${property.id}`
              : `/services/rent/properties/${property.id}`;
          const pcmRent = monthlyRentPcm(
            property.price,
            property.rent_frequency
          );
          const isAvailable =
            property.status?.toLowerCase() === "available" || !property.status;

          return (
            <article
              key={property.id}
              className="flex flex-col lg:flex-row bg-white mb-6 border border-solid border-[rgba(0,0,0,0.06)] shadow-sm transition-all duration-300 cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-brand-yellow/40"
              onClick={() =>
                window.open(`/services/rent/properties/${property.id}`, "_blank")
              }
            >
              <div className="w-full h-48 lg:w-[38%] lg:h-auto lg:min-h-[220px] overflow-hidden relative flex-shrink-0">
                <SafeImage
                  src={getMainImage(property as Property)}
                  alt={property.display_address}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <div
                  className="absolute top-3 left-3 font-manrope text-[10px] uppercase tracking-wider px-2 py-1 bg-white/95 text-text-obsidian"
                  onClick={(e) => e.stopPropagation()}
                >
                  {property.status || "Available"}
                </div>
                <div
                  className="absolute top-3 right-3 z-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <PropertySaveButton propertyId={property.id} />
                </div>
              </div>

              <div className="flex-1 p-5 lg:p-6 flex flex-col gap-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <h3 className="font-teko text-lg leading-tight text-text-obsidian mb-1">
                      {property.display_address}
                    </h3>
                    <p className="font-manrope text-xs uppercase tracking-wider text-text-slate">
                      {property.property_type}
                      {property.bedrooms != null
                        ? ` • ${property.bedrooms} Bed`
                        : ""}
                      {property.bathrooms != null
                        ? ` • ${property.bathrooms} Bath`
                        : ""}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-teko text-xl leading-none text-brand-yellow">
                      £{pcmRent.toLocaleString()}
                    </div>
                    <span className="font-manrope text-[10px] uppercase tracking-wider text-text-slate">
                      PCM
                    </span>
                    {property.rent_frequency !== "Monthly" ? (
                      <p className="font-manrope text-[10px] text-text-slate mt-0.5">
                        Listed £{property.price?.toLocaleString()}{" "}
                        {property.rent_frequency}
                      </p>
                    ) : null}
                  </div>
                </div>

                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-t-solid border-t-[rgba(0,0,0,0.06)] pt-4">
                  <div>
                    <dt className="font-manrope text-[10px] uppercase tracking-wider text-text-slate mb-0.5">
                      Sq Ft
                    </dt>
                    <dd className="font-teko text-base text-text-obsidian">
                      {property.internal_area
                        ? property.internal_area.toLocaleString()
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-manrope text-[10px] uppercase tracking-wider text-text-slate mb-0.5">
                      Deposit
                    </dt>
                    <dd className="font-teko text-base text-text-obsidian">
                      {property.deposit != null
                        ? `£${property.deposit.toLocaleString()}`
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-manrope text-[10px] uppercase tracking-wider text-text-slate mb-0.5">
                      Available
                    </dt>
                    <dd className="font-manrope text-sm text-text-obsidian leading-snug">
                      {getAvailabilityLabel(
                        property.status,
                        property.available_now,
                        property.date_available
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-manrope text-[10px] uppercase tracking-wider text-text-slate mb-0.5">
                      Status
                    </dt>
                    <dd
                      className={`font-teko text-base ${
                        isAvailable ? "text-green-600" : "text-orange-500"
                      }`}
                    >
                      {property.status || "Available"}
                    </dd>
                  </div>
                </dl>

                <div
                  className="flex justify-between items-center gap-3 pt-1 flex-wrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <PropertyShareButtons
                    title={`${property.display_address} — Letting Engine`}
                    url={propertyUrl}
                  />
                  <div className="flex items-center gap-2">
                    <a
                      href={propertyBrochureUrl(property.id)}
                      aria-disabled={!property.brochure_available}
                      onClick={(event) => {
                        if (!property.brochure_available) event.preventDefault();
                      }}
                      className={`inline-flex items-center gap-2 border border-solid border-text-obsidian px-4 py-2 font-teko text-sm uppercase tracking-wider transition-colors duration-300 ${
                        property.brochure_available
                          ? "text-text-obsidian hover:bg-brand-yellow hover:border-brand-yellow"
                          : "text-text-slate border-gray-300 cursor-not-allowed opacity-60"
                      }`}
                      title={
                        property.brochure_available
                          ? "Download property brochure PDF"
                          : "Brochure is being prepared"
                      }
                    >
                      <Download size={15} aria-hidden="true" />
                      Brochure PDF
                    </a>
                    <a
                      href={`/services/rent/properties/${property.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-text-obsidian text-white px-5 py-2 font-teko text-sm uppercase tracking-wider transition-colors duration-300 hover:bg-brand-yellow hover:text-text-obsidian"
                    >
                      View Specs
                    </a>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>
      <EmailAlertForm />
      <TenantGuides />
      <AreaGuideTeaser />
      <AboutAndContactForm />
      <CompareTray />
    </div>
  );
};

export default ListViewPage;
